/**
 * NUED link prefetch — viewport driven
 *
 * Speculation Rules の eagerness は immediate 以外すべて操作(ホバー等)が前提のため、
 * タッチデバイスでは実質発火しない。IG 内蔵ブラウザが主要流入である以上、
 * ホバーに依存しない先読みが必要。
 *
 * 方式:
 *   IntersectionObserver で画面内に入ったリンクを検出し、順次先読みする。
 *   マウス / タッチ / ブラウザ種別を問わず動作する。
 *
 * 実装:
 *   <link rel="prefetch"> が使える環境ではそれを使い、
 *   未対応 (Safari) では fetch() で HTTP キャッシュと接続を温める。
 */
(function () {
  if (typeof document === 'undefined') return;

  var conn = navigator.connection;
  if (conn && (conn.saveData || /(^|[^4])2g/.test(conn.effectiveType || ''))) return;
  if (!('IntersectionObserver' in window)) return;

  /* 先読み総数の上限。多すぎると初期表示の帯域を奪う */
  var LIMIT = 10;
  /* 画面内に入ってから実行するまでの猶予。素通りしたリンクは読まない */
  var DWELL_MS = 300;
  /* 同時実行数 */
  var MAX_INFLIGHT = 2;

  var EXCLUDE = /^\/(cart|checkouts?|account|apps|tools|services)(\/|$)/;
  /* 優先度: 商品ページとコレクションのみ。フッターの規約類などは読まない */
  var INCLUDE = /^\/(products|collections)(\/|$)/;

  var seen = new Set();
  var queue = [];
  var timers = new WeakMap();
  var inflight = 0;

  var supportsLinkPrefetch = (function () {
    try {
      var l = document.createElement('link');
      return l.relList && l.relList.supports && l.relList.supports('prefetch');
    } catch (e) {
      return false;
    }
  })();

  function targetUrl(link) {
    if (!link || !link.href) return null;
    if (link.target && link.target !== '_self') return null;
    if (link.hasAttribute('download')) return null;
    if (link.dataset.noPrefetch !== undefined) return null;

    var url;
    try {
      url = new URL(link.href, location.href);
    } catch (e) {
      return null;
    }

    if (url.origin !== location.origin) return null;
    if (url.pathname === location.pathname) return null;
    if (EXCLUDE.test(url.pathname)) return null;
    if (!INCLUDE.test(url.pathname)) return null;
    if (seen.has(url.href)) return null;

    return url.href;
  }

  function pump() {
    while (inflight < MAX_INFLIGHT && queue.length && seen.size < LIMIT) {
      run(queue.shift());
    }
  }

  function done() {
    inflight--;
    pump();
  }

  function run(href) {
    if (seen.has(href) || seen.size >= LIMIT) return;
    seen.add(href);
    inflight++;

    if (supportsLinkPrefetch) {
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = href;
      link.addEventListener('load', done, { once: true });
      link.addEventListener('error', done, { once: true });
      document.head.appendChild(link);
      /* load が来ない環境用の保険 */
      setTimeout(function () {
        if (link.dataset.settled === undefined) {
          link.dataset.settled = '1';
          done();
        }
      }, 5000);
      return;
    }

    fetch(href, {
      method: 'GET',
      credentials: 'include',
      redirect: 'follow',
      priority: 'low',
      headers: { purpose: 'prefetch' },
    })
      .then(function (res) { return res.text(); })
      .catch(function () {})
      .then(done);
  }

  function enqueue(href) {
    if (seen.has(href) || queue.indexOf(href) !== -1) return;
    queue.push(href);
    pump();
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var link = entry.target;

        if (!entry.isIntersecting) {
          clearTimeout(timers.get(link));
          return;
        }

        var href = targetUrl(link);
        if (!href) {
          observer.unobserve(link);
          return;
        }

        timers.set(
          link,
          setTimeout(function () {
            enqueue(href);
            observer.unobserve(link);
          }, DWELL_MS)
        );
      });
    },
    { rootMargin: '200px 0px' }
  );

  function scan() {
    if (seen.size >= LIMIT) return;
    document.querySelectorAll('a[href]').forEach(function (link) {
      if (targetUrl(link)) observer.observe(link);
    });
  }

  function boot() {
    scan();
    /* 無限スクロール / セクション再描画に追従 */
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        if (seen.size >= LIMIT) { mo.disconnect(); return; }
        scan();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* prerender 中は先読みを開始しない（無駄な多重取得を避ける） */
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', boot, { once: true });
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
